/**
 * Cyrillic → Latin transliteration map and slug utilities for SEO-friendly URLs.
 * Slug format: transliterate(name)-{id}
 * Example: "Филадельфия Классик" (id: 123) → "filadelfiya-klassik-123"
 */

export const CYRILLIC_MAP: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

/**
 * Transliterate a string from Cyrillic to Latin.
 */
export function transliterate(text: string): string {
  return text
    .toLowerCase()
    .split('')
    .map((char) => CYRILLIC_MAP[char] ?? char)
    .join('');
}

/**
 * Generate an SEO-friendly slug from a product name and id.
 * Transliterates Cyrillic, replaces spaces with hyphens,
 * removes special characters, and appends the id.
 *
 * @example generateSlug("Филадельфия Классик", 123) → "filadelfiya-klassik-123"
 */
export function generateSlug(name: string, id: number): string {
  const transliterated = transliterate(name);
  const slug = transliterated
    .replace(/\s+/g, '-')       // spaces → hyphens
    .replace(/[^a-z0-9-]/g, '') // remove non-alphanumeric (except hyphens)
    .replace(/-+/g, '-')        // collapse multiple hyphens
    .replace(/^-|-$/g, '');     // trim leading/trailing hyphens
  return `${slug}-${id}`;
}

/**
 * Extract the numeric id from the end of a slug.
 *
 * @example parseSlugId("filadelfiya-klassik-123") → 123
 */
export function parseSlugId(slug: string): number {
  const lastHyphen = slug.lastIndexOf('-');
  if (lastHyphen === -1) {
    throw new Error(`Invalid slug format: "${slug}"`);
  }
  const idStr = slug.slice(lastHyphen + 1);
  const id = Number(idStr);
  if (!Number.isInteger(id) || idStr === '') {
    throw new Error(`Invalid slug id: "${slug}"`);
  }
  return id;
}
